import React from "react";
import { Box, Card, CardContent, Typography, IconButton } from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

// Extract YouTube video ID from any standard YouTube URL
function getYouTubeId(url) {
  if (!url) return null;
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/
  );
  return match ? match[1] : null;
}

export default function RecentEvents({ config }) {
  if (!config || config.length === 0) return null;

  return (
    <Box sx={{ py: 8, px: { xs: 2, md: 6 }, background: "#faf6f2", position: "relative" }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 5 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: "#BF0603",
            fontFamily: "Quicksand",
            letterSpacing: 2,
            textTransform: "uppercase",
            mb: 1
          }}
        >
          Relive Our Gatherings
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            textAlign: "center",
            color: "#111",
            fontFamily: "Quicksand",
            fontSize: { xs: "26px", md: "36px" },
          }}
        >
          Our Recent Swayamvaram Highlights
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "#666",
            fontFamily: "Varela Round",
            maxWidth: "600px",
            mx: "auto",
            mt: 1.5,
            fontSize: "0.95rem"
          }}
        >
          Watch short video clips from our recently concluded offline meetups across different cities in Tamil Nadu.
        </Typography>
      </Box>

      {/* Swiper Carousel */}
      <Box sx={{ position: "relative", px: { xs: 0, md: 4 } }}>
        {/* ⬅️ Custom Prev Button */}
        <IconButton
          className="swiper-button-prev"
          sx={{
            position: "absolute",
            top: "50%",
            left: "-15px",
            transform: "translateY(-50%)",
            zIndex: 10,
            backgroundColor: "#fff",
            boxShadow: "0 6px 15px rgba(0,0,0,0.1)",
            width: 42,
            height: 42,
            border: "1px solid rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": { 
              backgroundColor: "#BF0603",
              "& svg": { color: "#fff" }
            },
            display: { xs: "none", md: "flex" }
          }}
        >
          <ArrowBackIosNew sx={{ color: "#BF0603", fontSize: 18, ml: -0.3 }} />
        </IconButton>

        {/* ➡️ Custom Next Button */}
        <IconButton
          className="swiper-button-next"
          sx={{
            position: "absolute",
            top: "50%",
            right: "-15px",
            transform: "translateY(-50%)",
            zIndex: 10,
            backgroundColor: "#fff",
            boxShadow: "0 6px 15px rgba(0,0,0,0.1)",
            width: 42,
            height: 42,
            border: "1px solid rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": { 
              backgroundColor: "#BF0603",
              "& svg": { color: "#fff" }
            },
            display: { xs: "none", md: "flex" }
          }}
        >
          <ArrowForwardIos sx={{ color: "#BF0603", fontSize: 18, mr: -0.3 }} />
        </IconButton>

        {/* 🎥 Swiper Carousel */}
        <Swiper
          modules={[Navigation, Autoplay]}
          spaceBetween={30}
          slidesPerView={3}
          navigation={{ nextEl: ".swiper-button-next", prevEl: ".swiper-button-prev" }}
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          loop
          breakpoints={{
            1200: { slidesPerView: 3 },
            850: { slidesPerView: 2 },
            600: { slidesPerView: 2 },
            0: { slidesPerView: 1 },
          }}
          style={{ paddingBottom: "50px", paddingTop: "10px" }}
        >
          {config.map((item, index) => {
            const videoId = getYouTubeId(item.link);
            // YouTube serves thumbnails at no cost via their CDN — no download required
            const thumbnailUrl = videoId
              ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
              : null;

            return (
              <SwiperSlide key={index}>
                <Card
                  sx={{
                    overflow: "hidden",
                    borderRadius: "20px",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.04)",
                    background: "#fff",
                    maxWidth: 400,
                    mx: "auto",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid rgba(0,0,0,0.04)",
                    transition: "all 0.4s ease",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 15px 35px rgba(191, 6, 3, 0.08)",
                      border: "1px solid rgba(191, 6, 3, 0.12)",
                      "& .play-icon": { transform: "scale(1.2)", color: "#fff" },
                      "& .play-bg": { backgroundColor: "#BF0603" }
                    }
                  }}
                  onClick={() => window.open(item.link, "_blank")}
                >
                  {/* Thumbnail */}
                  <Box sx={{ width: "100%", height: 210, overflow: "hidden", position: "relative", backgroundColor: "#1a1a1a" }}>
                    {thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt={item.title}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    ) : (
                      <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#2a2a2a" }}>
                        <PlayCircleOutlineIcon sx={{ fontSize: 60, color: "#888" }} />
                      </Box>
                    )}

                    {/* Dark gradient overlay */}
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background: "linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.08) 60%, transparent 100%)",
                      }}
                    />

                    {/* Centred play button */}
                    <Box
                      className="play-bg"
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 54,
                        height: 54,
                        borderRadius: "50%",
                        backgroundColor: "rgba(255,255,255,0.92)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
                        transition: "background-color 0.3s ease",
                      }}
                    >
                      <PlayCircleOutlineIcon
                        className="play-icon"
                        sx={{ fontSize: 34, color: "#BF0603", transition: "transform 0.3s ease, color 0.3s ease" }}
                      />
                    </Box>

                    {/* Watch on YouTube pill */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 10,
                        left: 10,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        backgroundColor: "rgba(0,0,0,0.65)",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "20px",
                        color: "#fff"
                      }}
                    >
                      <PlayCircleOutlineIcon sx={{ fontSize: 14 }} />
                      <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, fontFamily: "Quicksand" }}>
                        Watch on YouTube
                      </Typography>
                    </Box>
                  </Box>

                  <CardContent
                    sx={{
                      background: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 75,
                      p: 3
                    }}
                  >
                    <Typography
                      sx={{
                        textAlign: "center",
                        color: "#333",
                        fontSize: "0.95rem",
                        lineHeight: "1.4",
                        fontFamily: "Quicksand",
                        fontWeight: 700
                      }}
                    >
                      {item.title}
                    </Typography>
                  </CardContent>
                </Card>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </Box>
    </Box>
  );
}
